import { parseDate, roundToXDecimals } from '@ais-ui/utils';
import { QueryPointResult } from './queryPointResult';

export interface RadarFusedTargetInfo {
  // Position
  latitude: number;
  longitude: number;
  latitudeDegrees: string;
  longitudeDegrees: string;

  // Navigation
  speed: string;
  heading: string;
  course: string;

  // Identity (present in both flavours as track ID)
  mmsi: string;

  // AIS fields (only present when fused with AIS data)
  name: string;
  aisClass: string;
  country: string;
  flag: string;
  length: string;
  width: string;

  // Sensor info
  sensorId: string;
  sensorType: string;
  sensorTrackId: string;
  sensorMainSource: string;
  sensorSite: string;

  // Reporting
  lastReportTimestamp: Date | null;
  lastOrigin: string;
  targetType: string;

  /** True when AIS data is fused with radar, false for radar-only */
  isFused: boolean;
}

const desiredTargetIds: string[] = [
  'mmsi',
  'name',
  'ais_class',
  'country',
  'flag',
  'length',
  'width',
  'lat_last_report',
  'lon_last_report',
  'speed',
  'heading',
  'course',
  'datetime_last_report',
  'sensor_0_id',
  'sensor_0_type',
  'sensor_0_track_id',
  'sensor_0_main',
  'sensor_0_site',
  'lastorigin',
];

export function extractRadarFusedTargetInfos(
  result: QueryPointResult,
): RadarFusedTargetInfo[] {
  const radarFusedTargetInfos: RadarFusedTargetInfo[] = [];

  for (const target of result.targetInfo) {
    const raw: { [key: string]: string } = {};

    for (const targetValue of target.values) {
      if (desiredTargetIds.includes(targetValue.id)) {
        raw[targetValue.id] = targetValue.value;
      }
    }

    const mmsi = raw['mmsi'] || target['mmsi'] || '';
    const name = raw['name'] || '';
    const aisClass = raw['ais_class'] || '';

    const info: RadarFusedTargetInfo = {
      latitude: target['lat']
        ? roundToXDecimals(target['lat'], 4)
        : target['lat'],
      longitude: target['lon']
        ? roundToXDecimals(target['lon'], 4)
        : target['lon'],
      latitudeDegrees: raw['lat_last_report'] || '',
      longitudeDegrees: raw['lon_last_report'] || '',

      speed: raw['speed'] || '',
      heading: raw['heading'] || '',
      course: raw['course'] || '',

      mmsi,
      name,
      aisClass,
      country: raw['country'] || '',
      flag: raw['flag'] || '',
      length: raw['length'] || '',
      width: raw['width'] || '',

      sensorId: raw['sensor_0_id'] || '',
      sensorType: raw['sensor_0_type'] || '',
      sensorTrackId: raw['sensor_0_track_id'] || '',
      sensorMainSource: raw['sensor_0_main'] || '',
      sensorSite: raw['sensor_0_site'] || '',

      lastReportTimestamp: parseDate(raw['datetime_last_report']),
      lastOrigin: raw['lastorigin'] || '',
      targetType: target['type'] || '',

      isFused: !!(name || aisClass),
    };

    radarFusedTargetInfos.push(info);
  }

  return radarFusedTargetInfos;
}
